import React, { useEffect, useState, useCallback } from "react";
import Loaders from "../Loaders";
import HeaderTop from "../HeaderTop";
import Menus from "../Menus";
import Footers from "../Footers";
import Nulls from "../Nulls";
import SubscriberCountTable from "./SubscriberCountTable";
import SubscriberCountFilter from "./SubscriberCountFilter";
import * as XLSX from "xlsx/xlsx.mjs";
import format from "date-fns/format";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { API_ROUTES } from "../../../utils/constants";
import { use } from "react";

export default function SubscriberCount(props) {
  const { objectToFormData, capitalizeWords, isAuthExpired } = props;

  useEffect(() => {
    document.title = "Sub/Unsub Count Report | Forthsoft";
  }, []);

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const [day, month, year] = dateStr.split("-");
    return new Date(year, month - 1, day);
  };

  const formatDateForAPI = (date) => {
    if (!date) return "";
    return format(date, "dd-MM-yyyy");
  };

  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [dataList, setDataList] = useState([]);
  const [detailedData, setDetailedData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isFade, setIsFade] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [reportType, setReportType] = useState("");
  const [isDataAvailable, setIsDataAvailable] = useState(false);
  const [startDate, setStartDate] = useState(parseDate(""));
  const [endDate, setEndDate] = useState(parseDate(""));
  const [serviceList, setServiceList] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);

  const columns1 = [
    {
      name: "Date",
      selector: (row) => (row.date ? row.date : <Nulls />),
      sortable: true,
    },
    {
      name: "Total Count",
      selector: (row) =>
        row.count ? parseFloat(row.count).toLocaleString() : "0",
      sortable: true,
    },
    {
      name: "Bearer IDs",
      selector: (row) =>
        row.bearer_count ? `${row.bearer_count} Bearer IDs` : "0",
      sortable: false,
    },
  ];

  const detailedColumns = [
    {
      name: "Date",
      selector: (row) => (row.date ? row.date : <Nulls />),
      sortable: true,
    },
    {
      name: "Bearer ID",
      selector: (row) => (row.bearerId ? row.bearerId : <Nulls />),
      sortable: true,
    },
    {
      name: "Count",
      selector: (row) =>
        row.count ? parseFloat(row.count).toLocaleString() : "0",
      sortable: true,
    },
  ];

  useEffect(() => {
    setColumns(columns1);
  }, []);

  const userData = JSON.parse(localStorage.getItem("userDataFs"));

  useEffect(() => {
    const getServices = async () => {
      const user_id = localStorage.getItem("user_id");
      setLoadingServices(true);
      try {
        const response = await fetch(API_ROUTES.VIEW_SERVICES, {
          method: "POST",
        
          headers: { token: userData?.responseDetails?.token },
        });
        const responseJSON = await response.json();
        isAuthExpired(responseJSON);
        if (responseJSON.status.toString() === "200") {
          const unique = responseJSON.data.filter((s, i, arr) =>
            i === arr.findIndex((x) => x.serviceId === s.serviceId)
          );
          setServiceList(unique);
        }
      } catch (e) {
        console.log("Error fetching services:", e);
      } finally {
        setLoadingServices(false);
      }
    };
    getServices();
  }, []);

  const getSubscriberData = async (startDateStr, endDateStr, reportType, serviceId, networkId) => {
    if (!startDateStr || !endDateStr) {
      console.error("Missing date parameters");
      return;
    }

    const dataString = {
      fro: startDateStr,
      to: endDateStr,
      type: reportType,
      network_id: networkId || "all",
    };

    if (serviceId) dataString.service_id = serviceId;

    setIsFade(true);
    setIsLoading(true);
    setReportType(reportType);
    setIsDataAvailable(false);

    try {
      const userData = JSON.parse(localStorage.getItem("userDataFs"));
      const response = await fetch(API_ROUTES.SUBSCRIBERS_COUNTS, {
        method: "POST",
        body: objectToFormData(dataString),
        headers: { token: userData?.responseDetails?.token },
      });

      const responseJSON = await response.json();
      isAuthExpired(responseJSON);

      if (responseJSON.status.toString() === "200") {
        const responseData = responseJSON.data;

        // Transform data for main table and chart
        const transformedData = responseData.map((item) => ({
          date: item.date,
          count: item.count,
          bearer_count: item.bearer_ids ? item.bearer_ids.length : 0,
          bearer_ids: item.bearer_ids || [],
        }));

        // Create detailed breakdown data for expandable table
        const detailedBreakdown = [];
        responseData.forEach((dayData) => {
          if (dayData.bearer_ids && dayData.bearer_ids.length > 0) {
            dayData.bearer_ids.forEach((bearerItem) => {
              detailedBreakdown.push({
                date: dayData.date,
                bearerId: bearerItem.bearerId,
                count: bearerItem.count,
              });
            });
          }
        });

        setDataList(transformedData);
        setDetailedData(detailedBreakdown);
        if (detailedBreakdown.length > 0) {
          setIsDataAvailable(true);
        }

        const total = transformedData.reduce(
          (sum, item) => sum + item.count,
          0
        );
        setTotalCount(total);
      } else {
        console.log("Error response:", responseJSON);
        setDataList([]);
        setDetailedData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.log("Error getting subscriber data: ", error);
      setDataList([]);
      setDetailedData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      setIsFade(false);
    }
  };

  const handleFilterSubmit = async (startDate, endDate, reportType, serviceId, networkId) => {
    await getSubscriberData(startDate, endDate, reportType, serviceId, networkId);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "10px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <p>
            <strong>Date:</strong> {label}
          </p>
          <p>
            <strong>Total Count:</strong> {data.count.toLocaleString()}
          </p>
          <p>
            <strong>Bearer IDs:</strong> {data.bearer_count}
          </p>
          {data.bearer_ids && data.bearer_ids.length > 0 && (
            <div>
              <p>
                <strong>Breakdown:</strong>
              </p>
              {data.bearer_ids.slice(0, 5).map((bearer, index) => (
                <p key={index} style={{ fontSize: "12px", margin: "2px 0" }}>
                  {bearer.bearerId}: {bearer.count}
                </p>
              ))}
              {data.bearer_ids.length > 5 && (
                <p style={{ fontSize: "12px", fontStyle: "italic" }}>
                  ...and {data.bearer_ids.length - 5} more
                </p>
              )}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const downloadSheet = (jsonData) => {
   

    const workSheet = XLSX.utils.json_to_sheet(jsonData);
    const workBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workBook,
      workSheet,
      `${reportType === "unsubscribers" ? "unsubs" : "subs"} ${formatDateForAPI(
        startDate
      )} to ${formatDateForAPI(endDate)}`
    );
    XLSX.utils.sheet_add_aoa(
      workSheet,
      [["Date", "Bearer Ids", "Total Count"]],
      { origin: "A1" }
    );
    //count
    const max_width = jsonData.reduce((w, r) => Math.max(w, r.date.length), 10);
    workSheet["!cols"] = [{ wch: max_width }];
    XLSX.writeFile(workBook, "forthsoft_subscribers.xlsx");
  };

  const handleDownload = () => {
    setIsDownloading(true);

    downloadSheet(detailedData);

    setIsDownloading(false);
  };

  return (
    <>
      <Loaders />

      <div className="flapt-page-wrapper mainContent">
        <Menus />

        <div className="flapt-page-content">
          <HeaderTop />

          <div className="main-content introduction-farm mt--20">
            <div className="content-wraper-area">
              <div className="dashboard-area">
                <div className="container-fluid mb-90">
                  <div className="row g-4">
                    <div className="col-lg-12 pl-xs-0 pr-xs-0">
                      <div className="card-header mb--20 mb-xs-10">
                        <h2>Sub/Unsub Count Report</h2>
                      </div>
                      <div className="App">
                        <SubscriberCountFilter
                          submitQuery={handleFilterSubmit}
                          defaultStartDate=""
                          defaultEndDate=""
                          startDate={startDate}
                          setStartDate={setStartDate}
                          endDate={endDate}
                          setEndDate={setEndDate}
                          isDataAvailable={isDataAvailable}
                          isDownloading={isDownloading}
                          handleDownload={handleDownload}
                          serviceList={serviceList}
                          loadingServices={loadingServices}
                        />
                      </div>
                      {!isLoading && dataList.length > 0 && (
                        <div className="card mb-4 mt-30">
                          <div className="card-body">
                            <div className="row mb-4">
                              <div className="col-md-3 mb-3">
                                <div className="card bg-primary text-white">
                                  <div className="card-body">
                                    <h5>Total Count</h5>
                                    <h3>{totalCount.toLocaleString()}</h3>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-3 mb-3">
                                <div className="card bg-info text-white">
                                  <div className="card-body">
                                    <h5>Report Type</h5>
                                    <h3>{capitalizeWords(reportType)}</h3>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-3 mb-3">
                                <div className="card bg-success text-white">
                                  <div className="card-body">
                                    <h5>Total Days</h5>
                                    <h3>{dataList.length}</h3>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-3 mb-3">
                                <div className="card bg-warning text-white">
                                  <div className="card-body">
                                    <h5>Unique Bearer IDs</h5>
                                    <h3>
                                      {
                                        new Set(
                                          detailedData.map(
                                            (item) => item.bearerId
                                          )
                                        ).size
                                      }
                                    </h3>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div
                              style={{ height: "400px", marginBottom: "20px" }}
                            >
                              <h5>Daily Count Trend</h5>
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                  data={dataList}
                                  margin={{
                                    top: 5,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="date" />
                                  <YAxis />
                                  <Tooltip content={<CustomTooltip />} />
                                  <Legend />
                                  <Line
                                    type="monotone"
                                    dataKey="count"
                                    name="Daily Count"
                                    stroke="#8884d8"
                                    activeDot={{ r: 8 }}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        </div>
                      )}

                      <SubscriberCountTable
                        capitalizeWords={capitalizeWords}
                        dataList={dataList}
                        detailedData={detailedData}
                        isLoading={isLoading}
                        columns={columns}
                        detailedColumns={detailedColumns}
                        isFade={isFade}
                        isAuthExpired={isAuthExpired}
                        objectToFormData={objectToFormData}
                        onFilterSubmit={handleFilterSubmit}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Footers />
          </div>
        </div>
      </div>
    </>
  );
}
